import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Calendar as CalendarIcon, CheckSquare, ChevronLeft, Inbox as InboxIcon,
  RefreshCw, UsersRound, Video, MapPin, AlertCircle,
} from "lucide-react";
import { Avatar, Badge, Button, EmptyState, Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatDateTime, formatRelative, formatTime } from "@/lib/format";
import { invalidateUsers, useUsers } from "@/lib/use-users";
import { cn } from "@/lib/utils";
import { ChannelIcon } from "@/features/inbox/ChannelPicker";
import type { User } from "@/lib/types";

/**
 * Team — one page per person.
 *
 * `/team` lists everyone with a CRM login (the workspace's members; the
 * backend syncs new members in on every read). `/team/:userId` is that
 * person's desk: the tasks assigned to them, their upcoming appointments
 * (assigned to them, or on a booking calendar they own) and the open
 * conversations they're handling — the three things a teammate needs at a
 * glance, without a separate CRM per person.
 */

interface WorkTask {
  id: string; title: string; due_at: string | null; status: string; priority: string;
  contact_id: string | null; contact_name: string | null;
}
interface WorkAppointment {
  id: string; title: string; starts_at: string; ends_at: string; status: string;
  contact_id: string | null; contact_name: string | null;
  calendar_name: string | null; calendar_color: string | null;
  location: string | null; meeting_url: string | null;
}
interface WorkConversation {
  id: string; channel: string; subject: string | null; unread_count: number;
  last_message_at: string | null; last_message_preview: string | null;
  contact_id: string | null; contact_name: string | null; contact_avatar: string | null;
}
interface Work {
  user: User;
  tasks: WorkTask[];
  appointments: WorkAppointment[];
  conversations: WorkConversation[];
}

export function TeamPage() {
  const { userId } = useParams<{ userId?: string }>();
  return userId ? <PersonView userId={userId} /> : <TeamList />;
}

function TeamList() {
  const toast = useToast();
  const { users, loading, refresh } = useUsers();
  const [syncing, setSyncing] = useState(false);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post<{ sync: { ok: boolean; created: { email: string }[]; error?: string } }>("/settings/users/sync");
      invalidateUsers();
      await refresh(true);
      if (!r.sync.ok) toast.error(r.sync.error ? `Couldn't check the workspace: ${r.sync.error}` : "Couldn't check the workspace members");
      else if (r.sync.created.length > 0) toast.success(`Added ${r.sync.created.map((c) => c.email).join(", ")}`);
      else toast.success("Everyone in the workspace already has a CRM login");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Team</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Everyone with a login here. Open a person to see their tasks, appointments and conversations.
          </p>
        </div>
        <Button variant="ghost" leftIcon={<RefreshCw className={cn("h-4 w-4", syncing && "animate-spin")} />} onClick={sync} loading={syncing}>
          Sync from workspace
        </Button>
      </div>

      {loading && users.length === 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : users.length === 0 ? (
        <EmptyState icon={UsersRound} title="No team members yet" description="Invite people to the workspace in SyncSuite (Members), then sync." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {users.map((u) => (
            <Link
              key={u.id}
              to={`/team/${u.id}`}
              className="flex items-center gap-3 rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4 transition-colors hover:border-zinc-700 hover:bg-zinc-900/60"
            >
              <Avatar name={u.name} email={u.email} src={u.image} size="md" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-zinc-100">{u.name || u.email}</p>
                <p className="truncate text-xs text-zinc-500">{u.email}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
      <p className="text-xs text-zinc-500">
        Someone missing? They need to accept the workspace invite in SyncSuite first. Who each person sends email as
        is set under <Link to="/settings" className="text-indigo-300 hover:underline">Settings → Team</Link>.
      </p>
    </div>
  );
}

function PersonView({ userId }: { userId: string }) {
  const toast = useToast();
  const [work, setWork] = useState<Work | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setWork(await api.get<Work>(`/settings/users/${userId}/work`, { days: 30 }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [userId]);

  if (loading && !work) return <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-64" /></div>;
  if (!work) return <EmptyState icon={UsersRound} title="Not found" description="This person isn't on the team." />;

  const now = Date.now();
  const overdue = work.tasks.filter((t) => t.due_at && new Date(t.due_at).getTime() < now).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/team" className="grid h-8 w-8 place-items-center rounded-lg text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100">
          <ChevronLeft className="h-4 w-4" />
        </Link>
        <Avatar name={work.user.name} email={work.user.email} src={work.user.image} size="lg" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-semibold text-zinc-100 sm:text-2xl">{work.user.name || work.user.email}</h1>
          <p className="mt-0.5 text-sm text-zinc-500">
            {work.tasks.length} open task{work.tasks.length === 1 ? "" : "s"}
            {overdue > 0 && <span className="text-rose-400"> · {overdue} overdue</span>}
            {" · "}{work.appointments.length} upcoming · {work.conversations.length} open conversation{work.conversations.length === 1 ? "" : "s"}
          </p>
        </div>
        <Button variant="ghost" size="sm" leftIcon={<RefreshCw className="h-3.5 w-3.5" />} onClick={() => void load()}>Refresh</Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Column title="Tasks" Icon={CheckSquare} count={work.tasks.length} link="/tasks">
          {work.tasks.length === 0 ? (
            <Empty>Nothing assigned right now.</Empty>
          ) : work.tasks.map((t) => {
            const late = t.due_at && new Date(t.due_at).getTime() < now;
            return (
              <li key={t.id} className="px-4 py-3">
                <p className="text-sm text-zinc-100">{t.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  {t.due_at && (
                    <span className={cn("inline-flex items-center gap-1", late ? "text-rose-400" : "text-zinc-500")}>
                      {late ? <AlertCircle className="h-3 w-3" /> : <CalendarIcon className="h-3 w-3" />}
                      {formatDateTime(t.due_at)}
                    </span>
                  )}
                  {t.priority !== "normal" && (
                    <Badge color={t.priority === "urgent" ? "rose" : t.priority === "high" ? "amber" : "zinc"} size="xs">{t.priority}</Badge>
                  )}
                  {t.contact_id && t.contact_name && (
                    <Link to={`/contacts/${t.contact_id}`} className="text-indigo-300 hover:underline">{t.contact_name}</Link>
                  )}
                </div>
              </li>
            );
          })}
        </Column>

        <Column title="Appointments" Icon={CalendarIcon} count={work.appointments.length} link="/calendar">
          {work.appointments.length === 0 ? (
            <Empty>Nothing booked in the next 30 days.</Empty>
          ) : work.appointments.map((a) => (
            <li key={a.id} className="px-4 py-3">
              <p className="text-sm text-zinc-100">{a.title}</p>
              <p className="mt-0.5 text-xs text-zinc-400">
                {formatDateTime(a.starts_at)} – {formatTime(a.ends_at)}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                {a.calendar_name && <Badge color={(a.calendar_color as never) ?? "indigo"} size="xs">{a.calendar_name}</Badge>}
                {a.contact_id && a.contact_name && (
                  <Link to={`/contacts/${a.contact_id}`} className="text-indigo-300 hover:underline">{a.contact_name}</Link>
                )}
                {a.meeting_url && (
                  <a href={a.meeting_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-300 hover:underline">
                    <Video className="h-3 w-3" /> Join
                  </a>
                )}
                {!a.meeting_url && a.location && (
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>
                )}
              </div>
            </li>
          ))}
        </Column>

        <Column title="Conversations" Icon={InboxIcon} count={work.conversations.length} link="/inbox">
          {work.conversations.length === 0 ? (
            <Empty>No open conversations assigned.</Empty>
          ) : work.conversations.map((c) => (
            <li key={c.id}>
              <Link to={c.contact_id ? `/inbox/${c.contact_id}` : "/inbox"} className="flex items-start gap-3 px-4 py-3 hover:bg-zinc-900/40">
                <Avatar name={c.contact_name ?? "?"} src={c.contact_avatar} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium text-zinc-100">{c.contact_name || "Unknown"}</p>
                    <ChannelIcon channel={c.channel} className="h-3 w-3 shrink-0 text-zinc-500" />
                    {c.unread_count > 0 && <Badge color="indigo" size="xs">{c.unread_count}</Badge>}
                  </div>
                  <p className="truncate text-xs text-zinc-500">{c.subject || c.last_message_preview || ""}</p>
                </div>
                {c.last_message_at && <span className="shrink-0 text-[10px] text-zinc-500">{formatRelative(c.last_message_at)}</span>}
              </Link>
            </li>
          ))}
        </Column>
      </div>
    </div>
  );
}

function Column({ title, Icon, count, link, children }: {
  title: string;
  Icon: React.ComponentType<{ className?: string }>;
  count: number;
  link: string;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      <div className="flex items-center gap-2 border-b border-zinc-800/40 px-4 py-2.5">
        <Icon className="h-3.5 w-3.5 text-zinc-400" />
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-300">{title}</span>
        <span className="ml-1 text-[10px] tabular-nums text-zinc-500">{count}</span>
        <div className="flex-1" />
        <Link to={link} className="text-[11px] text-indigo-300 hover:underline">Open</Link>
      </div>
      <ul className="divide-y divide-zinc-800/40">{children}</ul>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <li className="px-4 py-6 text-center text-xs text-zinc-500">{children}</li>;
}
